import { Filter } from 'pixi.js';
import { quadProgram } from '../filterQuad';

/**
 * **出血边缘的淡出。**
 *
 * ## 为什么必须有它
 *
 * 牌面纹理比牌本身大一圈（`metrics.FACE_PAD_RATIO`），所以伸到牌外的图形
 * 会在纹理边界被**硬裁**。而设计里恰恰有一张牌越界得离谱：异常牌的同心圆
 * 半径 78、圆心在牌外 5 个单位 —— 它远超出任何合理的出血宽度，
 * 硬裁的结果是牌外挂着一道垂直的直线切口，一眼假。
 *
 * 所以最外圈用 `smoothstep` 把 alpha 收到 0：图形不是被切断，是**淡出去**。
 *
 * ⚠️ 用 `smoothstep` 而不是线性。线性淡出在起点和终点都会留下一道
 * 肉眼可见的折线（一阶不连续），而这里要的正是"看不出边界在哪"。
 *
 * ⚠️ 只在**烘焙时**跑一次（见 `faceCache` 的两趟渲染），不是每帧的 filter。
 * 牌面是静态资产，画完就只剩一张纹理。
 *
 * ## 坐标：必须用 `vQuadCoord`，不能用 `vTextureCoord`
 *
 * 这一条是**踩过坑才定下来的**，别改回去。`vTextureCoord` 不是 [0,1]
 * （它是 `frame/源` 的比例），详见 `render/filterQuad.ts`。
 * 用它算"到纹理边还有多远"，`uSize - p` 永远到不了 0 ——
 *
 * | 边 | 修之前 | 修之后 |
 * | --- | --- | --- |
 * | 左 / 上 | 正常淡出 | 正常淡出 |
 * | 右 | 最后 7.5px 硬切 | 正常淡出 |
 * | 下 | 最后 **58px** 硬切 | 正常淡出 |
 *
 * （牌高 112 时纹理 120×168，纹理池给的是 128×256，
 * `vTextureCoord` 覆盖 `[0,0.9375] × [0,0.65625]`。）
 */
export const PAD_FADE_FRAGMENT = /* glsl */ `
in vec2 vTextureCoord;
in vec2 vQuadCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
/** 纹理的逻辑尺寸（不是物理像素）—— 长度一律用逻辑像素，和项目其它地方一致 */
uniform vec2  uSize;
/** 淡出宽度（逻辑像素）。0 = 不淡出 */
uniform float uFadePx;

void main(void) {
  vec4 color = texture(uTexture, vTextureCoord);

  // ⚠️ uFadePx <= 0 时**整段跳过**，不要靠 smoothstep 自己退化：
  //    smoothstep(0, 0, d) 在 GLSL 里是**未定义行为**（要除以 edge1-edge0）。
  //    调试页的"关掉淡出"就是靠传 0 来的，不能有 UB。
  if (uFadePx > 0.0) {
    // 到最近一条边的距离。⚠️ 用 vQuadCoord（[0,1] 铺满整块滤镜区域），
    //    不是 vTextureCoord（那个被 frame/源 的比例压过）
    vec2 p = vQuadCoord * uSize;
    float d = min(min(p.x, uSize.x - p.x), min(p.y, uSize.y - p.y));

    // ⚠️ 预乘 alpha：rgb 和 a 要**一起**缩放才保持预乘，
    //    只动 a 会让颜色在淡出区变亮（Pixi 的 colorMatrix 也是这个约定）
    color *= smoothstep(0.0, uFadePx, d);
  }

  finalColor = color;
}
`;

/** 淡出宽度相对牌宽的比例。8–10 逻辑像素是设计定的量级 */
export const PAD_FADE_RATIO = 0.08;

export interface PadFadeOptions {
  /** 纹理的逻辑尺寸 */
  readonly width: number;
  readonly height: number;
  /** 淡出宽度（逻辑像素） */
  readonly fadePx: number;
}

/**
 * 拿一个淡出 filter。
 *
 * ⚠️ **按参数复用，调用方不要 destroy。** 同一批牌面的纹理尺寸和淡出宽度都一样，
 * 一张牌造一个 `Filter` 是纯浪费 —— 第一版那样写，13 张牌预热要 147ms。
 * filter 的状态分成两半：`filterArea` 在**精灵**上（每个精灵各自的），
 * uniform 在 filter 上（同尺寸就是同一份），所以复用是安全的。
 */
const filterCache = new Map<string, Filter>();

export function padFadeFilter(options: PadFadeOptions): Filter {
  const key = `${String(options.width)}x${String(options.height)}@${String(options.fadePx)}`;
  const hit = filterCache.get(key);
  if (hit !== undefined) return hit;
  const made = new Filter({
    glProgram: quadProgram(PAD_FADE_FRAGMENT, 'card-pad-fade'),
    resources: {
      padFadeUniforms: {
        uSize: { value: new Float32Array([options.width, options.height]), type: 'vec2<f32>' },
        uFadePx: { value: options.fadePx, type: 'f32' },
      },
    },
  });
  filterCache.set(key, made);
  return made;
}
